import { toApiDate } from "@marekl/adminer/datetime";
import { pluralizeCzech } from "@marekl/adminer/format";
import type { Membership, MembershipRole, Person } from "../../../lib/queries/memberships";
import type { ID } from "../../../lib/id";
import { IDENTITY_FIELDS } from "../TeamDetailPage/memberFormSchema";
import { FIELD_LABELS, FIELDS, norm } from "./draftFields";
import { persistedRow, personToDraft, rosterSignature } from "./draftRows";
import {
  emptyPerson,
  type DraftPerson,
  type DraftRow,
  type DraftState,
  type FieldConflict,
  type RebaseNotice,
} from "./draftTypes";
import { isBlankNewRow, isRowDirty } from "./draftValidation";

/**
 * Merging a draft with the roster as the server has it NOW, instead of
 * throwing the draft away. docs/rozpracovana-registrace.md §6 is the table
 * this implements; the server applies the same per-field rule on submit
 * (membership.mergeOntoStored), so whatever this lets through is merged the
 * same way there.
 */

export interface RebaseInput {
  /** The active-season roster as just fetched. */
  members: Membership[];
  leaderPersonId: ID | null;
  /** The source season's candidates as just fetched: staged transfers merge against them. */
  candidates?: Membership[];
  /** admin / members_admin: the only ones who may overwrite a filled identity field. */
  canEditIdentity: boolean;
  /**
   * Memberships the draft had already seen besides its own rows: a restored
   * draft keeps only the rows with work, so it says separately which roster
   * it was built against.
   */
  known?: ID[];
}

function setField<K extends keyof DraftPerson>(p: DraftPerson, f: K, v: DraftPerson[K]): void {
  p[f] = v;
}

function sameSet(a: readonly MembershipRole[], b: readonly MembershipRole[]): boolean {
  return a.length === b.length && a.every((r) => b.includes(r));
}

/** Twin of membership.mergeRoles: stored, minus what the draft took away, plus what it added. */
export function mergeRoles(
  stored: readonly MembershipRole[],
  base: readonly MembershipRole[],
  value: readonly MembershipRole[],
): MembershipRole[] {
  const out = stored.filter((r) => !(base.includes(r) && !value.includes(r)));
  for (const r of value) if (!base.includes(r) && !out.includes(r)) out.push(r);
  return out;
}

/**
 * Whether this reader may NOT overwrite `field` once the server holds
 * `theirs` for it: the twin of the server's identity lock (filled identity
 * is immutable below members_admin, the leader's always). The server still
 * decides; this only keeps the page from offering a choice it would refuse.
 */
export function identityLocked(
  field: keyof DraftPerson,
  row: DraftRow,
  theirs: DraftPerson,
  canEditIdentity: boolean,
): boolean {
  if (canEditIdentity) return false;
  if (!(IDENTITY_FIELDS as readonly string[]).includes(field)) return false;
  return row.isLeader || norm(field, theirs[field]) !== "";
}

interface RowResult {
  row: DraftRow;
  /** Fields the server changed and the draft had not: taken silently. */
  refreshed: boolean;
  newConflicts: (keyof DraftPerson)[];
  /** Conflicts decided for the server because this viewer may not overwrite it. */
  tookTheirs: (keyof DraftPerson)[];
}

/**
 * rebaseRow is the three-way merge of one row: base (row.baseline, what the
 * draft was edited from), mine (row.person) and theirs (the server now).
 * Afterwards the server value is the row's new baseline, so the submit's
 * person_base matches what is stored and the server merges nothing again.
 */
export function rebaseRow(
  row: DraftRow,
  theirsPerson: Person,
  theirRoles: readonly MembershipRole[],
  canEditIdentity: boolean,
): RowResult {
  const base = row.baseline ? personToDraft(row.baseline) : emptyPerson;
  const theirs = personToDraft(theirsPerson);
  const person: DraftPerson = { ...row.person };
  const conflicts: DraftRow["conflicts"] = {};
  const newConflicts: (keyof DraftPerson)[] = [];
  const tookTheirs: (keyof DraftPerson)[] = [];
  let refreshed = false;

  for (const f of FIELDS) {
    const b = norm(f, base[f]);
    const m = norm(f, row.person[f]);
    const t = norm(f, theirs[f]);
    // A conflict still open from an earlier merge stays open: the server
    // keeping its value since is no answer to the question it asked.
    const open = row.conflicts[f] !== undefined;
    if (m === t) continue;
    if (m === b && !open) {
      setField(person, f, theirs[f]);
      refreshed = true;
      continue;
    }
    if (t === b && !open) continue;
    if (identityLocked(f, row, theirs, canEditIdentity)) {
      setField(person, f, theirs[f]);
      tookTheirs.push(f);
      continue;
    }
    conflicts[f] = { theirs: theirs[f] } satisfies FieldConflict;
    if (!open) newConflicts.push(f);
  }

  // A row staged for removal sends no fields, so there is nothing to decide:
  // its values simply follow the server.
  if (row.removed) {
    for (const f of Object.keys(conflicts) as (keyof DraftPerson)[]) setField(person, f, theirs[f]);
    newConflicts.length = 0;
    tookTheirs.length = 0;
    for (const f of Object.keys(conflicts)) delete conflicts[f as keyof DraftPerson];
  }

  const rolesTouched = !sameSet(row.roles, row.baselineRoles);
  const roles = rolesTouched ? mergeRoles(theirRoles, row.baselineRoles, row.roles) : [...theirRoles];
  if (!rolesTouched && !sameSet(roles, row.roles)) refreshed = true;

  return {
    row: {
      ...row,
      baseline: theirsPerson,
      baselineRoles: [...theirRoles],
      person,
      roles,
      conflicts,
      // They spoke about a submit against the old roster.
      serverErrors: {},
    },
    refreshed,
    newConflicts,
    tookTheirs,
  };
}

function nameOf(row: DraftRow): string {
  return `${row.person.last_name} ${row.person.first_name}`.trim() || "Nový člen";
}

function sameIdentity(p: DraftPerson, q: Person): boolean {
  const fold = (s: string) => s.trim().toLocaleLowerCase("cs");
  const date = toApiDate(p.birth_date);
  return (
    Boolean(date) &&
    date === q.birth_date &&
    fold(p.first_name) !== "" &&
    fold(p.first_name) === fold(q.first_name) &&
    fold(p.last_name) === fold(q.last_name)
  );
}

function personIdOf(row: DraftRow): ID | null {
  return row.origin.kind === "new" ? null : row.origin.personId;
}

export function rebaseDraft(state: DraftState, input: RebaseInput): DraftState {
  const { members, leaderPersonId, canEditIdentity } = input;
  const notices: RebaseNotice[] = [];
  let refreshedCount = 0;

  const draftByMembership = new Map<ID, DraftRow>();
  const transferByPerson = new Map<ID, DraftRow>();
  /** Draft roster rows whose membership is gone, by person: they may be back under a new one. */
  const goneByPerson = new Map<ID, DraftRow>();
  const live = new Set(members.map((m) => m.id));
  const knewMembership = new Set<ID>(input.known ?? []);
  for (const r of state.rows) {
    if (r.origin.kind === "persisted") {
      draftByMembership.set(r.origin.membershipId, r);
      knewMembership.add(r.origin.membershipId);
      if (!live.has(r.origin.membershipId) && !r.removed) goneByPerson.set(r.origin.personId, r);
    } else if (r.origin.kind === "transfer") {
      transferByPerson.set(r.origin.personId, r);
    }
  }
  const used = new Set<string>();

  const note = (res: RowResult) => {
    if (res.refreshed) refreshedCount++;
    if (res.newConflicts.length > 0) {
      notices.push({
        key: res.row.key,
        message: `${nameOf(res.row)}: ${res.newConflicts.map((f) => FIELD_LABELS[f]).join(", ")} mezitím změnil někdo jiný. Vyberte, která hodnota platí.`,
      });
    }
    if (res.tookTheirs.length > 0) {
      notices.push({
        key: res.row.key,
        message: `${nameOf(res.row)}: ${res.tookTheirs.map((f) => FIELD_LABELS[f]).join(", ")} mezitím vyplnil správce členské základny. Platí uložená hodnota, vaši už změnit nelze.`,
      });
    }
  };

  // The roster as the server has it now, each line carrying whatever the
  // draft had staged for it.
  const rows: DraftRow[] = [];
  for (const m of members) {
    const fresh = persistedRow(m, leaderPersonId);
    const mine = draftByMembership.get(m.id);
    if (mine) {
      used.add(mine.key);
      const res = rebaseRow({ ...mine, isLeader: fresh.isLeader, orphaned: false }, m.person, m.roles, canEditIdentity);
      note(res);
      rows.push(res.row);
      continue;
    }
    const staged = transferByPerson.get(m.person.id) ?? goneByPerson.get(m.person.id);
    if (staged && !used.has(staged.key)) {
      // Somebody registered the person meanwhile. The staged transfer
      // becomes an edit of that row: the server would refuse the transfer
      // („už je na soupisce"), and the leader's corrections still apply.
      used.add(staged.key);
      const res = rebaseRow(
        { ...staged, key: fresh.key, origin: fresh.origin, isLeader: fresh.isLeader, orphaned: false },
        m.person,
        m.roles,
        canEditIdentity,
      );
      note(res);
      notices.push({
        key: res.row.key,
        message: `${nameOf(res.row)}: osobu mezitím zaregistroval někdo jiný. Vaše úpravy jsme přenesli na její řádek.`,
      });
      rows.push(res.row);
      continue;
    }
    rows.push(fresh);
  }

  const candidates = new Map((input.candidates ?? []).map((c) => [c.person.id, c]));
  for (const r of state.rows) {
    if (used.has(r.key)) continue;
    const personId = personIdOf(r);
    const isLeader = personId != null && personId === leaderPersonId;
    switch (r.origin.kind) {
      case "persisted": {
        // The membership is gone from the roster.
        if (r.removed) continue; // so was the intent
        if (!isRowDirty(r) && !r.orphaned) {
          notices.push({ key: null, message: `${nameOf(r)}: osoba byla mezitím odebrána ze soupisky.` });
          continue;
        }
        if (!r.orphaned) {
          notices.push({
            key: r.key,
            message: `${nameOf(r)}: osoba byla mezitím odebrána ze soupisky. Rozhodněte, zda ji zaregistrovat znovu.`,
          });
        }
        rows.push({ ...r, isLeader, orphaned: true, serverErrors: {} });
        break;
      }
      case "transfer": {
        const c = candidates.get(r.origin.personId);
        if (!c) {
          rows.push({ ...r, isLeader });
          break;
        }
        // A transfer has no roles to merge: they are the leader's pick.
        const res = rebaseRow({ ...r, isLeader }, c.person, r.baselineRoles, canEditIdentity);
        note(res);
        rows.push({ ...res.row, roles: r.roles, baselineRoles: r.baselineRoles });
        break;
      }
      case "new": {
        if (isBlankNewRow(r)) continue; // withTrailingBlank puts the free row back
        // Only a person who APPEARED since the draft last saw the roster:
        // one who was there all along is somebody the leader saw and chose
        // to type in anyway, and „Ponechat jako novou osobu" must stick.
        const twin = members.find((m) => !knewMembership.has(m.id) && sameIdentity(r.person, m.person));
        if (twin && r.duplicateOf !== twin.id) {
          notices.push({
            key: r.key,
            message: `${nameOf(r)}: na soupisce mezitím přibyla osoba se stejným jménem a datem narození.`,
          });
          rows.push({ ...r, duplicateOf: twin.id });
          break;
        }
        rows.push(r);
        break;
      }
    }
  }

  if (refreshedCount > 0) {
    notices.unshift({
      key: null,
      message: `Načetli jsme změny, které mezitím udělal někdo jiný (${refreshedCount} ${pluralizeCzech(refreshedCount, ["osoba", "osoby", "osob"])}).`,
    });
  }

  return {
    ...state,
    rows,
    hydrated: true,
    leaderPersonId,
    rosterSignature: rosterSignature(members),
    formMessage: null,
    rebaseNotices: notices,
  };
}

/**
 * mergeDuplicate folds a typed-in person into the roster row of the same
 * person: every value the roster row lacks is filled from the typed one, and
 * a value both have and disagree on becomes a conflict, decided like any
 * other. The typed row is dropped.
 */
export function mergeDuplicate(state: DraftState, key: string, canEditIdentity: boolean): DraftState {
  const typed = state.rows.find((r) => r.key === key);
  const targetId = typed?.duplicateOf;
  if (!typed || !targetId) return state;
  const rows = state.rows
    .filter((r) => r.key !== key)
    .map((r) => {
      if (r.origin.kind !== "persisted" || r.origin.membershipId !== targetId) return r;
      const person: DraftPerson = { ...r.person };
      const conflicts: DraftRow["conflicts"] = { ...r.conflicts };
      for (const f of FIELDS) {
        const v = norm(f, typed.person[f]);
        const t = norm(f, r.person[f]);
        if (v === "" || v === t) continue;
        if (t === "") {
          setField(person, f, typed.person[f]);
          continue;
        }
        if (identityLocked(f, r, r.person, canEditIdentity)) continue;
        setField(person, f, typed.person[f]);
        conflicts[f] = { theirs: r.person[f] };
      }
      const roles = [...r.roles];
      for (const role of typed.roles) if (!roles.includes(role)) roles.push(role);
      return { ...r, person, conflicts, roles };
    });
  return { ...state, rows };
}

